import { Component, ViewChild, effect } from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { GlobalsService, SleepService, AuthService, LoginService, HttpService, LookupService, TasksService, UsageService, UsagePlatforms, AppVersionService } from '@services';
import { ClarityService } from '@services/clarity.service';
import { DevTraceService } from '@services/utils/dev-trace.service';
import { InactivityTimeoutService } from '@services/inactivity-timeout.service';
import { VersionReleaseDialogComponent } from '@shared-components/version-release-dialog';
import moment from 'moment';
import { RemindersStore, CorporateStore, CustomErrorsStore, NgxConfetttiExplosionStore, TasksStore, TimeZoneStore } from '@stores';
import { IVersionReleaseModel, IAnnouncementModel, IFeaturePopupModel } from '@models';
import { HeaderBarComponent } from './layout/header/header-bar.component';
import Swal from 'sweetalert2';

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
	standalone: false,
})
export class AppComponent {
	@ViewChild('dialogRef', { static: true }) dialogRef: VersionReleaseDialogComponent;
	@ViewChild(HeaderBarComponent) headerBar!: HeaderBarComponent;

	isLoggedIn: boolean = false;
	isCustomerForms: boolean = false;
	isSurvey: boolean = false;
	// Public New Account Setup form. Like the survey it is reached with no session, so it has to
	// bypass both the login redirect and the app shell.
	isNewAccountSetup: boolean = false;
	isQuickbooksOAuthResponse: boolean = false;
	isCleanClaimsAuthResponse: boolean = false;
	isGoogleCalendarAuthResponse: boolean = false;
	isOutlookCalendarAuthResponse: boolean = false;
	isOnlinePayments: boolean = false;
	isClientPortal: boolean = false;
	showPage: boolean = false;
	autoLogoutTimer: NodeJS.Timeout;
	showDialog: boolean = false;
	versionModels: IVersionReleaseModel[];
	announcement: IAnnouncementModel;
	showAnnouncementDialog: boolean = false;
	featurePopup: IFeaturePopupModel;
	showConfetti: boolean = false;

	isiFrame = !((sessionStorage.getItem('jwt_token') || '') === '');

	/**
	 * Starts the in-app appointment / task reminder timer.  The server decides what is next and when,
	 * off the user's own settings - see RemindersStore.
	 */
	private async startReminders() {
		try {
			await this.remindersStore.init();
		} catch {
			// A reminder that can't be timed shouldn't take the rest of startup with it.
		}
	}

	// Team chat is for staff only -- not admins, subcontractors or the embedded views -- and
	// only for users it's switched on for (the Team Chat Enabled box on the user setup page).
	// The GlobalsService flags are only ever set to true (on the matching host), so they read as
	// undefined for everybody else -- test them truthily rather than against false.
	showTeamChat: boolean = false;

	// Internal tool - only our own company (3) gets the floating theme picker.
	showThemeSwitcher: boolean = false;
	constructor(
		private router: Router,
		private authService: AuthService,
		private httpService: HttpService,
		private devTraceService: DevTraceService,
		private loginService: LoginService,
		private lookupService: LookupService,
		private corporateStore: CorporateStore,
		private customErrorsStore: CustomErrorsStore,
		private timeZoneStore: TimeZoneStore,
		private tasksStore: TasksStore,
		private remindersStore: RemindersStore,
		private ngxConfetttiExplosionStore: NgxConfetttiExplosionStore,
		private usageService: UsageService,
		private clarityService: ClarityService,
		private inactivityTimeoutService: InactivityTimeoutService,
		private appVersionService: AppVersionService
	) {
		this.isLoggedIn = GlobalsService.isLoggedIn;

		// A required version reaches us two ways -- the SignalR broadcast and the check at startup
		// (for a tab that wasn't open when the broadcast went out). Both land on the same signal,
		// so one effect serves both.
		//
		// Staff only. The client portal, online payments, customer forms and survey hosts all share
		// this component, and an "update JobSight" prompt in front of a CUSTOMER paying an invoice
		// or signing a form is meaningless to them and alarming besides -- they have nothing to
		// update. None of them should ever reach a floor anyway (it's only fetched once signed in,
		// and the hub needs a token), but this is the cheap belt to that braces.
		effect(() => {
			if (GlobalsService.isClientPortal || GlobalsService.isOnlinePayments) return;
			if (this.isCustomerForms || this.isSurvey) return;

			if (this.appVersionService.isUpdateRequired()) this.promptRequiredUpdate();
		});

		this.trackModuleUsage();

		this.showTeamChat =
			this.isLoggedIn === true &&
			this.isiFrame === false &&
			!GlobalsService.isAdmin &&
			!GlobalsService.isOnlinePayments &&
			!GlobalsService.isClientPortal &&
			!GlobalsService.userInfo?.isSubcontractor &&
			GlobalsService.userInfo?.teamChatEnabled === true;
		console.log(this.showTeamChat);

		this.showThemeSwitcher = this.isLoggedIn === true && this.isiFrame === false && GlobalsService.companyInfo?.companyId === 3;

		window.addEventListener('message', event => {
			if (event.data.type === 'SET_CORPORATE_TIME_ZONE') {
				GlobalsService.corporateTimeZone = event.data.value;
			}
		});

		this.ngxConfetttiExplosionStore.showExplostion$.subscribe(() => (this.showConfetti = true));

		// This code will trap all buttons and prevent the user from double clicking
		document.addEventListener('click', (e: MouseEvent) => {
			let buttonEl: HTMLButtonElement;

			// Go 3 elements deep to see if this is a button.
			// The first element is most likely going to be the text of the button
			const target = <HTMLElement>e.target;
			if (target.nodeName === 'BUTTON') buttonEl = <HTMLButtonElement>target;

			const parentTarget = <HTMLElement>(target === null ? null : target.parentElement);
			if (parentTarget && parentTarget.nodeName === 'BUTTON') buttonEl = <HTMLButtonElement>parentTarget;

			const grandparentTarget = <HTMLElement>(parentTarget === null ? null : parentTarget.parentElement);
			if (grandparentTarget && grandparentTarget.nodeName === 'BUTTON') buttonEl = <HTMLButtonElement>grandparentTarget;

			if (buttonEl) {
				if (buttonEl.hasAttribute('disabled') || buttonEl.hasAttribute('allowDoubleClick')) return;

				setTimeout(() => {
					if (buttonEl.querySelector('.slick-button-spinner_error')) return;

					buttonEl.setAttribute('disabled', 'disabled');
				});

				// If this has a slick-button-spinner, let the spinner re-enable
				if (!buttonEl.classList.contains('slick-button-spinner')) setTimeout(() => buttonEl.removeAttribute('disabled'), 600);
			}
		});

		if (GlobalsService.isOnlinePayments) {
			router.events.subscribe(async event => {
				if (event instanceof NavigationStart) {
					if (event.url.indexOf('/online-payments') !== 0) window.location.href = '/#/online-payments';
				}
			});

			// Set alongside showPage, not left for the NavigationStart handler: the template picks its
			// outlet off this flag, and showing the page before it's set rendered the logged-out outlet
			// first and then swapped it for the payments one.
			this.isOnlinePayments = true;
			this.showPage = true;
			return;
		}

		if (GlobalsService.isAdmin) {
			this.showPage = true;
			return;
		}
	}

	async ngAfterViewInit() {
		// Every time we change a page, make sure we can go there
		this.router.events.subscribe(async event => {
			if (event instanceof NavigationStart) {
				await SleepService.sleep();

				// Coerced, not copied: these GlobalsService flags are only ever set to true on the matching
				// host, so they read as undefined everywhere else - and the template tests them against
				// false, which undefined fails, which blanks every outlet.
				this.isOnlinePayments = GlobalsService.isOnlinePayments === true;
				if (this.isOnlinePayments) {
					if (event.url.indexOf('/online-payments') !== 0) window.location.href = '/#/online-payments';
					this.showPage = true;
					return;
				}

				this.isClientPortal = GlobalsService.isClientPortal === true;
				if (this.isClientPortal) {
					if (window.location.href.indexOf('/client-portal') < 0) {
						const token = window.location.pathname.replace(/^\/+|\/+$/g, '');
						const query = token ? `?tl=${encodeURIComponent(token)}` : '';

						if (GlobalsService.isDebug) window.location.replace(`http://devportal.jobsight.com/#/client-portal${query}`);
						else window.location.replace(`https://${window.location.host}/#/client-portal${query}`);
						return;
					}

					this.showPage = true;
					return;
				}

				if (window.location.href.indexOf('/customer-forms') >= 0) {
					this.showPage = true;
					this.isCustomerForms = true;
					return;
				}

				if (window.location.href.indexOf('/quickbooksOAuthResponse') >= 0) {
					this.showPage = true;
					this.isQuickbooksOAuthResponse = true;
					return;
				}

				if (window.location.href.indexOf('/cleanClaimsAuthResponse') >= 0) {
					this.showPage = true;
					this.isCleanClaimsAuthResponse = true;
					return;
				}

				if (window.location.href.indexOf('/googleCalendarOAuthResponse') >= 0) {
					this.showPage = true;
					this.isGoogleCalendarAuthResponse = true;
					return;
				}

				if (window.location.href.indexOf('/outlookCalendarOAuthResponse') >= 0) {
					this.showPage = true;
					this.isOutlookCalendarAuthResponse = true;
					return;
				}

				if (window.location.href.indexOf('#/survey') >= 0) {
					this.showPage = true;
					this.isSurvey = true;
					return;
				}

				if (window.location.href.indexOf('#/new-account-setup') >= 0) {
					this.showPage = true;
					this.isNewAccountSetup = true;
					return;
				}

				if (GlobalsService.isLoggedIn === false) {
					if (event.url.toLowerCase().indexOf('/login') < 0) {
						if (event.url.toLowerCase().indexOf('/reset-password') >= 0 || event.url.toLowerCase().indexOf('/forgot-password') >= 0) {
							window.location.href = `/#${event.url}`;
							this.showPage = true;
							return;
						}
						await this.devTraceService.addTrace(`Logout 4: ${event.url.toLowerCase()}`);
						window.location.href = '/#/login';
						return;
					}
				}

				// If they've been disabled, force a logoff
				if (GlobalsService.userInfo.isDisabled === true) {
					await this.loginService.autoLogout();
					this.authService.clearToken();
					await this.devTraceService.addTrace('Logged out due to user disabled');
					window.location.href = '/';
					return;
				}

				// Without this, we get a little blip of the original page before we re-direct to
				this.showPage = true;
			}
		});

		if (GlobalsService.isLoggedIn) {
			if ((GlobalsService.isLearningPaths || GlobalsService.isRC || GlobalsService.isStaging || GlobalsService.isHotfix) && !GlobalsService.companySettings.isTestCompany) {
				Swal.fire({
					icon: 'warning',
					title: 'This is a test server',
					text: 'This is NOT the production server!!!  This is only used for internal JobSight testing',
					confirmButtonColor: '#007bff',
					width: '28em',
					heightAuto: false,
				});
			}
			this.inactivityTimeoutService.resetInactivityTimeout();

			// Initially read from localStorage, but then clear it and read from the server
			setTimeout(() => {
				if (sessionStorage.getItem('jwt_token')) this.lookupService.clearSessionstorage();
				else this.lookupService.clearLocalstorage();
			}, 5000);

			if (!GlobalsService.isAdmin) {
				this.autoLogoutTimer = setInterval(async () => await this.checkTimeout(), 60000);
				await this.checkTimeout();
			}

			const lastSeen = GlobalsService.userInfo.lastSeenVersion;
			const currentVersion = GlobalsService.appVersion;

			if (lastSeen !== currentVersion && (!lastSeen || lastSeen === '0' || this.normalizeVersion(lastSeen) < this.normalizeVersion(currentVersion))) {
				if (!lastSeen || lastSeen === '0') {
					await this.updateUserLastSeenVersion(currentVersion);
				} else {
					await this.showVersionHistory();
					await this.updateUserLastSeenVersion(currentVersion);
				}
			}

			await this.checkForAnnouncement();

			// Covers the session that wasn't open when a version was flagged required -- SignalR
			// only ever reaches whoever is connected at that moment.
			await this.appVersionService.refresh();

			// Deliberately not awaited - the popup drifts in shortly after the page settles and
			// shouldn't hold up the rest of the startup work.
			this.checkForFeaturePopup();

			this.tasksStore.init(false);

			// Appointment reminders are timed here rather than pushed - see the store. Not awaited: it
			// fetches the user's next couple of days of appointments, which nothing else is waiting on.
			this.startReminders();

			await this.customErrorsStore.init();

			if (GlobalsService.isCorporate) {
				await this.corporateStore.init();
				await this.timeZoneStore.init();
			}
		}
		// If not logged in, clear local storage
		else {
			if (sessionStorage.getItem('jwt_token')) this.lookupService.clearSessionstorage();
			else this.lookupService.clearLocalstorage();
		}
	}

	/**
	 * Records which module the user is in on every navigation, and starts Clarity once they're
	 * signed in.
	 *
	 * Driven off navigation rather than off the login flow because that's the one place every
	 * route change passes through, whichever way the user got there - login, deep link, or back
	 * button. Both services no-op until GlobalsService.isLoggedIn is true, so nothing is recorded
	 * on the login screen and no session is ever unattributable.
	 */
	private trackModuleUsage() {
		this.usageService.start(UsagePlatforms.Desktop);

		this.router.events.subscribe(event => {
			if (!(event instanceof NavigationEnd)) return;

			if (GlobalsService.isLoggedIn === false) return;

			// Safe to call repeatedly - it injects the Clarity script once.
			this.clarityService.start();

			const route = (event.urlAfterRedirects || event.url || '').split('?')[0].replace(/^\//, '');
			const modul = route.split('/')[0];

			if (modul) this.usageService.trackPageView(modul, route);
		});
	}

	//	| Version | Normalized |
	//| ----------- | ------------- |
	//| `"6.4.9"` | `"006004009"` |
	//| `"6.4.10"` | `"006004010"` |
	//| `"10.5.12"` | `"010005012"` |

	normalizeVersion(version: string): string {
		return version
			.split('.')
			.map(num => num.padStart(3, '0'))
			.join('');
	}

	private requiredUpdatePromptOpen = false;
	// This build is below a required version. There's no live-update download on desktop -- a plain
	// reload is what picks up new code -- so "Update Now" just reloads the page. "Later" leaves them
	// alone for this session; the next reload gets it anyway.
	private async promptRequiredUpdate() {
		if (this.requiredUpdatePromptOpen) return;
		this.requiredUpdatePromptOpen = true;

		const response = await Swal.fire({
			icon: 'warning',
			title: 'Update Required',
			html: `A new update is available.` + `<div class="swal-note">Certain features might not work until it is updated. Reloading will lose any unsaved changes.</div>`,
			showConfirmButton: true,
			confirmButtonText: '<i class="far fa-arrows-rotate"></i> Update Now',
			confirmButtonColor: '#007bff',
			showCancelButton: true,
			cancelButtonText: '<i class="far fa-clock"></i> Later',
			allowOutsideClick: false,
			width: '28em',
			heightAuto: false,
		});

		this.requiredUpdatePromptOpen = false;

		if (response.isConfirmed) window.location.reload();
	}

	private async checkTimeout() {
		if (GlobalsService.isLoggedIn) {
			const lastAction = localStorage.getItem('last_action');
			if (lastAction) {
				const lastActionDate = moment(new Date(parseInt(lastAction)));
				if (moment().diff(lastActionDate, 'hours') > 12) {
					GlobalsService.isLoggedIn = false;
					clearInterval(this.autoLogoutTimer);
					await this.loginService.autoLogout();
					this.authService.clearToken();
					await this.devTraceService.addTrace('Logout because of timeout');
					window.location.href = '/';
					return;
				}
			}
		} else {
			await this.devTraceService.addTrace('Logout - checkTimeout but already logged out');
			GlobalsService.isLoggedIn = false;
			clearInterval(this.autoLogoutTimer);
			await this.loginService.autoLogout();
			this.authService.clearToken();
		}
	}

	async checkForAnnouncement() {
		const lastSeenId = GlobalsService.userInfo.lastSeenAnnouncement;
		const params: any = {};
		if (lastSeenId != null) params.lastSeenAnnouncementId = lastSeenId;

		const announcement: IAnnouncementModel = await this.httpService.get('/announcement/getAnnouncementForDialog', params);

		if (!announcement) return;

		this.announcement = announcement;
		this.showAnnouncementDialog = true;
	}

	// Floats a random one-line feature blurb up from the bottom of the page a couple of seconds
	// after a refresh - at most once every 4 hours per user in this browser, and only in the regular
	// JobSight app. Purely decorative, so any failure is swallowed rather than surfaced.
	async checkForFeaturePopup() {
		if (!this.canShowFeaturePopup()) return;

		await SleepService.sleep(2000);

		// Checked again after the wait: the router may have landed somewhere it shouldn't show, and an
		// announcement the user is still reading takes precedence.
		if (!this.canShowFeaturePopup() || this.showAnnouncementDialog) return;

		const storageKey = `featurePopupLastShown_${GlobalsService.userInfo?.userId}`;
		const fourHours = 4 * 60 * 60 * 1000;
		try {
			const lastShown = Number(localStorage.getItem(storageKey) || 0);
			if (Date.now() - lastShown < fourHours) return;
		} catch {}

		try {
			this.featurePopup = await this.httpService.get('/featurePopup/getRandomFeaturePopup');

			if (this.featurePopup) {
				try {
					localStorage.setItem(storageKey, Date.now().toString());
				} catch {}
			}
		} catch {}
	}

	// The feature popup is for the regular JobSight app only - not the pages other people reach
	// (online card payments, the client portal, customer forms, surveys), subcontractor users, global
	// admin, the app embedded in an iframe, or a window the app opened itself (the QuickBooks, Clean
	// Claims, Google and Outlook sign-in windows).  The URL checks matter because those windows share the
	// logged-in session, so they run the normal start-up path.
	private canShowFeaturePopup(): boolean {
		if (GlobalsService.isAdmin || GlobalsService.isOnlinePayments || GlobalsService.isClientPortal) return false;
		if (GlobalsService.userInfo?.isSubcontractor) return false;
		if (this.isiFrame || window.opener) return false;

		const url = window.location.href.toLowerCase();
		const excludedPages = [
			'/online-payments',
			'/client-portal',
			'/customer-forms',
			'/survey',
			'/quickbooksoauthresponse',
			'/cleanclaimsauthresponse',
			'/googlecalendaroauthresponse',
			'/outlookcalendaroauthresponse',
			'/login',
			'/forgot-password',
			'/reset-password',
		];

		return !excludedPages.some(page => url.indexOf(page) >= 0);
	}

	async onAnnouncementDialogClose() {
		this.showAnnouncementDialog = false;
		if (this.announcement) {
			const id = this.announcement.announcementId;
			await this.httpService.patch(`/announcement/updateUserLastSeenAnnouncement?announcementId=${id}`);
			GlobalsService.userInfo.lastSeenAnnouncement = id;
		}
	}

	// Checked at the moment of the call, not just at startup - the version dialog is awaited first,
	// and the user can be logged out (auto-logout, another tab) while it is open.
	private async updateUserLastSeenVersion(currentVersion: string) {
		if (!GlobalsService.isLoggedIn || !GlobalsService.jwtToken) return;

		await this.httpService.patch(`/versionRelease/updateUserLastSeenVersion?versionNumber=${currentVersion}`);
	}

	async showVersionHistory() {
		if (GlobalsService.userInfo.isDispatch || GlobalsService.userInfo.isCorporateAdmin) return;

		const params: any = {
			lastSeenVersion: GlobalsService.userInfo.lastSeenVersion,
			appVersion: GlobalsService.appVersion,
		};

		this.versionModels = await this.httpService.get('/versionRelease/getAllVersionReleasesForDialog', params);
		if (this.versionModels.length === 0) return;

		this.showDialog = true;
	}

	onVersionDialogClose() {
		if (this.dialogRef?.dialogElement) {
			const dialogElement = this.dialogRef.dialogElement.nativeElement;

			// If we are in corporte view or some other view, we may not even have a release notes button.
			// If not, just close the dialog without animation.
			const releaseNotesButton = this.headerBar.getReleaseNotesButton();
			if (!releaseNotesButton) {
				this.showDialog = false;
				return;
			}

			const buttonRect = this.headerBar.getReleaseNotesButton().nativeElement.getBoundingClientRect();
			const dialogRect = dialogElement.getBoundingClientRect();
			let offset = 10;
			const translateX = buttonRect.left - dialogRect.left + offset; // Adjust as needed
			const translateY = buttonRect.top - dialogRect.top + buttonRect.height / 2 - dialogRect.height / 2;

			dialogElement.style.transition = 'transform 0.7s cubic-bezier(0.8, 0, 0.2, 1), opacity 0.7s ease';
			dialogElement.style.transformOrigin = 'left center';

			// Start the fade-out animation along with the translation
			dialogElement.style.animation = 'fadeOut 0.7s forwards'; // Use the fadeOut keyframes
			dialogElement.style.transform = `translate(${translateX}px, ${translateY}px) scale(0.00)`; // Shrink and move the dialog

			setTimeout(() => {
				this.showDialog = false;
				dialogElement.style.transition = 'none';
				dialogElement.style.transform = 'translate(0, 0) scale(1)';
				dialogElement.style.opacity = '1'; // Reset opacity after closing
				dialogElement.style.animation = ''; // Remove the animation

				// Trigger the "pop" effect on the version release button after the dialog closes
				const buttonElement = this.headerBar.getReleaseNotesButton()?.nativeElement;
				if (buttonElement) {
					buttonElement.style.transition = 'transform 0.3s ease';
					buttonElement.style.transform = 'scale(1.1)'; // Slightly enlarge the button
					setTimeout(() => {
						buttonElement.style.transform = 'scale(1)'; // Return to normal size
					}, 300);
				}
			}, 700); // Match the timing of the animation
		}
	}
}
